import type { ReactNode } from 'react'
import styles from './EmptyState.module.css'

/**
 * What a screen says when it has nothing to show.
 *
 * The usual empty state is a cartoon and a shrug: an illustration of an empty
 * box and the words "Nothing here yet". It fails at the only job it has. An
 * empty table is, more often than not, somebody's first minute inside a feature
 * they have not used before, and it is the one moment on that screen when there
 * is nothing else competing for their attention. It is the best place in the
 * whole product to explain something, and the cartoon spends it on a joke.
 *
 * So the shape here is fixed: a headline that names what belongs in this space,
 * a body that says how the first one gets there, and one action that starts it.
 * The illustration is a slot and it is optional, because it is the least useful
 * part.
 *
 * Two states that look identical and are not: a list that is empty because
 * nothing exists yet, and a list that is empty because the current filter
 * excludes everything. The second one needs different words and a way back, and
 * the component cannot tell them apart, so the caller passes the right copy and
 * the right action. Getting this wrong is how a person concludes their project
 * has no cases when it has four hundred and a date range set to last week.
 */

export interface EmptyStateProps {
  headline: string
  body?: ReactNode
  /** A mark, a glyph, a small drawing. Rendered above the headline. */
  illustration?: ReactNode
  /** The one thing to do next. */
  action?: ReactNode
  /** The quieter way out: clearing a filter, reading the guidance. */
  secondaryAction?: ReactNode
  /**
   * `inline` sits inside a table's own empty row, where the surrounding panel
   * already provides the frame and the vertical room is somebody else's.
   *
   * `variant` and not `tone`. This is where the drawing sits, not what state it
   * reports, and `tone` in this library means exactly one thing: the five-name
   * status union in `primitives/tone.ts`, mapped once at the edge and passed
   * through unchanged. A second meaning for the word is how somebody ends up
   * writing `tone="danger"` here and getting a page-sized empty state.
   */
  variant?: 'page' | 'inline'
  className?: string
}

export function EmptyState({
  headline,
  body,
  illustration,
  action,
  secondaryAction,
  variant = 'page',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={[styles.empty, variant === 'inline' ? styles.inline : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {illustration ? (
        <div className={styles.illustration} aria-hidden="true">
          {illustration}
        </div>
      ) : null}

      {/*
        A `p`, not an `h2`. This is not a section heading and the screen already
        has one; announcing a second heading here puts a landmark in the outline
        that leads to an explanation of why a list is empty. The serif carries
        the emphasis instead, which is the register the design system reserves
        for the product speaking rather than labelling.
      */}
      <p className={styles.headline}>{headline}</p>

      {body ? <p className={styles.body}>{body}</p> : null}

      {action || secondaryAction ? (
        <div className={styles.actions}>
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  )
}
